/**
 * <es-buy-together> - adds every product of a Buy together bundle to the cart in one request.
 *
 * The markup is a real cart form (items[][id] / items[][quantity] hidden inputs posting to the cart), so the bundle
 * still works without JavaScript. With it, this element sends the same items through the theme's CartAPI - which
 * asks for the cart drawer and cart count sections, so every CartAPI subscriber (the drawer, the header count)
 * refreshes - then opens the cart drawer, or goes to the cart page when the page has no drawer.
 *
 * A refused add (sold out meanwhile, quantity limits) shows Shopify's own message, or the theme's generic cart error,
 * in the bundle's alert region. UI strings come from the markup; nothing user-facing is written here.
 *
 * Shared "add all" engine: Shop the look and Product bundles render the same form around Product Cards. Their inputs
 * carry data-variant-from="<container id>", so the variant chosen on each card is the one added (see
 * syncCardVariants); data-unavailable-message is shown when a card's current choice cannot be bought.
 */
class ESBuyTogether extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.busy = false;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('button[type="submit"]');
    this.label = this.querySelector('[data-label]');
    this.spinner = this.querySelector('[data-spinner]');
    this.error = this.querySelector('[data-error]');

    this.form?.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.form = null;
  }

  async onSubmit(event) {
    // Without the theme's cart script the form simply posts to the cart page.
    if (!window.CartAPI || !window.Shopify?.routes) return;
    event.preventDefault();
    if (this.busy) return;

    const inputs = Array.from(this.form.querySelectorAll('[data-variant-id]'));
    if (!this.syncCardVariants(inputs)) {
      this.showError(this.dataset.unavailableMessage || this.dataset.errorMessage || '');
      return;
    }

    const items = inputs
      .map((input) => ({ id: Number(input.value), quantity: 1 }))
      .filter((item) => item.id > 0);
    if (items.length === 0) return;

    this.setBusy(true);
    this.showError('');

    try {
      await window.CartAPI.performCartRequest(
        `${window.Shopify.routes.root}cart/add.js`,
        JSON.stringify({ items, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
      );

      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else if (this.dataset.cartUrl) {
        window.location.assign(this.dataset.cartUrl);
      }
    } catch (error) {
      this.showError(error?.description || this.dataset.errorMessage || '');
    } finally {
      this.setBusy(false);
    }
  }

  /* An input may name a container (data-variant-from="<id>") holding a Product Card. The card's <quick-add> carries
     the variant the shopper has chosen on it (its option buttons keep variant-id current), so the bundle adds that
     variant rather than the one rendered with the page. A card that offers no Quick Add (gift cards, selling plans,
     the Soft card) keeps the rendered variant. Returns false when a card shows an unavailable choice: the bundle is
     not added at all rather than added in part. */
  syncCardVariants(inputs) {
    let available = true;

    inputs.forEach((input) => {
      const sourceId = input.dataset.variantFrom;
      if (!sourceId) return;

      const quickAdd = document.getElementById(sourceId)?.querySelector('quick-add');
      if (!quickAdd) return;

      // The card's button is disabled for an unavailable variant, and also while that card is adding on its own
      // (its spinner is showing) - only the first means the choice cannot be bought.
      const variantId = quickAdd.getAttribute('variant-id');
      const button = quickAdd.querySelector('button');
      const spinner = quickAdd.querySelector('.product-card__action-btn-spinner');
      const busy = spinner ? !spinner.hidden : false;
      if (!variantId || (button?.disabled && !busy)) {
        available = false;
        return;
      }
      input.value = variantId;
    });

    return available;
  }

  setBusy(busy) {
    this.busy = busy;
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.setAttribute('aria-busy', String(busy));
    this.label?.toggleAttribute('hidden', busy);
    this.spinner?.toggleAttribute('hidden', !busy);
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = message === '';
  }
}

if (!customElements.get('es-buy-together')) {
  customElements.define('es-buy-together', ESBuyTogether);
}
